import type { Doc } from "building-types/doc";
import type { EntitySet } from "building-types/entities/entitySetTypes";
import { BuildingViewerViewMode } from "building-types/geometry/buildingGeometryTypes";
import type { FaceCategory } from "building-types/materials/faceCategories";
import { FaceDiffType, type BuildingEntityFaceHashMapsRenderSettings } from "building-types/materials/faceHashTypes";
import { ElevationNoteEntityDebugMode } from "building-types/views/elevationAnnotationTypes";
import type { View } from "building-types/views/GeneratedPageView";
import type { ViewFilter } from "building-types/views/viewFilterTypes";
import type { DocFaceDirection } from "building-types/walls2/wallPlaneFilters";
import type { CameraPosition } from "common/types/cameraPosition";
import { EnvPreset } from "gfx/EnvPreset";
import type { HighlightDataTexture } from "gfx/HighlightDataTexture";
import { defaultFaceHashCategories, type BuildingEntityFaceHashMaps } from "lib/house/buildingEntityFaceHashTypes";
import type { EditorDebugPrefs } from "lib/studio/editorTypes";

export { BuildingViewerViewMode };

export function isViewModeWallPlane(viewMode: BuildingViewerViewMode) {
  return viewMode === BuildingViewerViewMode.WALL_PLANE;
}

/** Elevation, section, and wall plane views share orthographic facade camera behavior. */
export function isViewModeOrthographicFacade(viewMode: BuildingViewerViewMode) {
  return viewMode === BuildingViewerViewMode.ELEVATION_OR_SECTION || viewMode === BuildingViewerViewMode.WALL_PLANE;
}

export function isViewModeOrthographic(viewMode: BuildingViewerViewMode) {
  return (
    viewMode === BuildingViewerViewMode.PLAN ||
    viewMode === BuildingViewerViewMode.ELEVATION_OR_SECTION ||
    viewMode === BuildingViewerViewMode.WALL_PLANE
  );
}

export enum InteriorLightMode {
  NONE,
  POINT_LIGHTS,
  LIGHT_OFFSET_MAP,
}

export interface SelectState {
  kind: "FACEDIFF_EDGE";
  id: string;
}

// Normally we render the site that's defined for the building. In case the site is not defined, we can fallback to either a plain ground plane or generic grass and trees.
export enum SiteType {
  GROUND_PLANE_DISABLE_SITE, // This option fully disables rendering the site that's defined for the building. This is used in sheet preview line renders.
  FALLBACK_TO_GROUND_PLANE,
  FALLBACK_TO_GRASS,
}

export type DebugSettingId =
  | "crossSectionGeometry"
  | "crossSectionRoofVolumes"
  | "crossSectionHoles"
  | "elevationColliders"
  | "interiorLights"
  | "interiorLightOffsetMaps"
  | "roofClippingPlanes"
  | "roofPerimeterRails"
  | "roofVolumes"
  | "roofInsideVolumes"
  | "roofDiscardVolumes"
  | "roofRidgeDiscardVolumes"
  | "roofDiscardWithoutDormersVolumes"
  | "roofOverlapVolumes"
  | "roofOverhangClippingVolumes"
  | "roofHalfStoryDiscardVolumes"
  | "disableRoofWalls"
  | "disableFriezeBoards"
  | "disableSoffits"
  | "disableFasciaBoards"
  | "disableFasciaTopTrim"
  | "disableRoofRafters"
  | "buildingOutlineVertices"
  | "logSceneGeometryStats"
  | "foundationIntersections"
  | "levelExtrusions"
  | "buildingGraph"
  | "wallStructuralDomainRoomConditionAdjacencies";

export type RenderDebugSettings = { [key in DebugSettingId]: boolean } & {
  showElevationNoteEntities: ElevationNoteEntityDebugMode;
};

export const defaultRenderDebugSettings: RenderDebugSettings = {
  crossSectionGeometry: false,
  crossSectionRoofVolumes: false,
  crossSectionHoles: false,
  elevationColliders: false,
  showElevationNoteEntities: ElevationNoteEntityDebugMode.NONE,
  interiorLights: false,
  interiorLightOffsetMaps: false,
  roofClippingPlanes: false,
  roofPerimeterRails: false,
  roofVolumes: false,
  roofInsideVolumes: false,
  roofDiscardVolumes: false,
  roofRidgeDiscardVolumes: false,
  roofDiscardWithoutDormersVolumes: false,
  roofOverlapVolumes: false,
  roofOverhangClippingVolumes: false,
  roofHalfStoryDiscardVolumes: false,
  disableRoofWalls: false,
  disableFriezeBoards: false,
  disableSoffits: false,
  disableFasciaBoards: false,
  disableFasciaTopTrim: false,
  disableRoofRafters: false,
  buildingOutlineVertices: false,
  logSceneGeometryStats: false,
  foundationIntersections: false,
  levelExtrusions: false,
  wallStructuralDomainRoomConditionAdjacencies: false,
  buildingGraph: false,
};

export const isBSPVolumeFilterKey = {
  roofVolumes: true,
  roofInsideVolumes: true,
  roofDiscardVolumes: true,
  roofRidgeDiscardVolumes: true,
  roofDiscardWithoutDormersVolumes: true,
  roofOverlapVolumes: true,
  roofOverhangClippingVolumes: true,
  roofHalfStoryDiscardVolumes: true,
} as Partial<Record<keyof RenderDebugSettings, boolean>>;

export const defaultRenderFaceDiffSettings: BuildingEntityFaceHashMapsRenderSettings = {
  faceHashGenerationMode: "WEB",
  faceDiffEntityMask: defaultFaceHashCategories,
  faceDiffTypeSettings: {
    [FaceDiffType.REMOVED]: {
      renderLines: true,
    },
  },
};

export const emptyRenderFaceDiffSettings: BuildingEntityFaceHashMapsRenderSettings = {
  faceHashGenerationMode: "WEB",
  faceDiffEntityMask: new Set(),
  faceDiffTypeSettings: {},
};

export const emptyEditorDebugPrefs: EditorDebugPrefs = {
  accumulateGeometry: false,
  entityIdSearch: false,
  faceDiffEnabled: false,
  debugElevationColliders: false,
  showElevationNoteEntities: ElevationNoteEntityDebugMode.NONE,
  debugFloorPlanRelaxation: false,
  faceDiffSettings: emptyRenderFaceDiffSettings,
  docRefDebugView: false,
};

export interface BuildingViewerSceneState {
  envPreset: EnvPreset;
  siteRotation: number; // Angular offset from current sunlight direction
  altitudeAzimuthOverride?: [number, number];

  interiorLightMode: InteriorLightMode;

  viewMode: BuildingViewerViewMode;

  // The direction that the camera is looking at in orthographic view. Should be defined for orthographic views.
  // If undefined, will default to a top down view.
  orthoViewDirX?: number;
  orthoViewDirY?: number;
  orthoViewDirZ?: number;

  elevationBottomClippingPlane?: number; // Used to clip out basement from elevation preview.

  exploreModeCamera?: CameraPosition;

  view?: View;

  selectedLevelId: string | null;
  selectState: SelectState | null;
  lutURL: string;
  devCameraControls: boolean;
  interiorAmbientLightEnabled: boolean;
  refractionProbesForFrostedGlass: boolean;
  interiorLightProbesEnabled: boolean;
  exteriorLightProbesEnabled: boolean;
  siteType: SiteType;

  highlightDataTexture: HighlightDataTexture | null;

  debugSettings: RenderDebugSettings;

  faceDiffEnabled: boolean;
  faceDiffSettings: BuildingEntityFaceHashMapsRenderSettings;
  debugFaceDiff?: BuildingEntityFaceHashMaps;

  viewFilter: Partial<ViewFilter>;

  resetCameraOnLevelChange: boolean;

  selectedReportingUnitLabelId?: string;
  hiddenCategoriesOutsideSelectedReportingUnit?: ReadonlySet<FaceCategory>; // Only supported for props and casework for now.
  overrideMaterialOnDeselectedReportingUnits: boolean;

  /**
   * Live underlay table and hidden-entity set. Both doc fields are flagged as not affecting
   * building generation, so `building.doc` keeps whichever copies existed at the last generation
   * and an underlay edit alone never regenerates. Editor contexts pass the current tables here;
   * where these are unset the building was generated from the current doc, so `building.doc` is
   * already accurate.
   */
  underlays?: Doc["underlays"];
  hiddenEntities?: Doc["hidden"];

  /** Editor selection (only set when rendered in editor context). */
  editorSelectState?: EntitySet;
  /** Editor preselection hover highlight (editor context only). */
  editorPreselectState?: EntitySet;

  /**
   * Wall-plane edit mode's plane/direction, set when that edit mode is hosted inside a `view` that
   * isn't itself a `WALL_PLANE_VIEW` (e.g. the elevation viewer's Edit Wall tab). The camera should
   * face and frame this plane instead of `view`'s own projection while set.
   */
  wallPlaneEditHostPlaneHash?: string;
  wallPlaneEditHostDirection?: DocFaceDirection;

  // Far clip (world units) for the wall-plane ortho camera preview. Set per-step via
  // StepDefinition.wallPlaneCameraDepthLimit (guided workflow); undefined leaves the camera's
  // default (effectively infinite) far plane.
  wallPlaneCameraDepthLimit?: number;
}

/**
 * Plane/direction wall-plane editing is scoped to: either a genuine `WALL_PLANE_VIEW`, or wall-plane
 * edit mode hosted inside another view (e.g. the elevation viewer's Edit Wall tab). Shared by any
 * render stage that needs to target the wall plane being edited regardless of which view hosts it
 * (camera framing, the gray fade-outside-plane stencil overlay, etc).
 */
export function sceneStateWallPlaneAndDirection(
  sceneState: BuildingViewerSceneState,
): { planeHash: string; direction: DocFaceDirection } | undefined {
  if (sceneState.view?.entityType === "WALL_PLANE_VIEW") {
    return { planeHash: sceneState.view.planeHash, direction: sceneState.view.direction };
  }
  if (sceneState.wallPlaneEditHostPlaneHash !== undefined && sceneState.wallPlaneEditHostDirection !== undefined) {
    return { planeHash: sceneState.wallPlaneEditHostPlaneHash, direction: sceneState.wallPlaneEditHostDirection };
  }
  return undefined;
}

export const defaultSceneState: BuildingViewerSceneState = Object.freeze({
  siteRotation: 0,
  envPreset: EnvPreset.MORNING,
  selectState: null,
  selectedLevelId: null,
  lutURL: "/static/lut/LUT_Standard.png",
  interiorLightMode: InteriorLightMode.LIGHT_OFFSET_MAP,
  showInteriorProps: true,
  showExteriorProps: true,
  siteType: SiteType.FALLBACK_TO_GRASS,
  highlightDataTexture: null,
  pickIdRegistry: {},
  viewMode: BuildingViewerViewMode.EXTERIOR,
  sectionView: undefined,
  devCameraControls: false,
  interiorAmbientLightEnabled: true,
  refractionProbesForFrostedGlass: false,
  interiorLightProbesEnabled: false,
  exteriorLightProbesEnabled: true,
  debugSettings: defaultRenderDebugSettings,
  faceDiffEnabled: false,
  faceDiffSettings: defaultRenderFaceDiffSettings,
  viewFilter: { labels: {} },
  resetCameraOnLevelChange: false,
  selectedReportingUnitLabelId: undefined,
  hiddenCategoriesOutsideSelectedReportingUnit: undefined,
  overrideMaterialOnDeselectedReportingUnits: true,
});
